import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { CheckCircle } from 'lucide-react';
import axios from 'axios';

import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { grievancesAPI } from '../utils/api';

const MAX_CHARACTERS = 500;

const GrievanceSubmission = () => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [referenceId, setReferenceId] = useState('');
  const [servicesMenu, setServicesMenu] = useState([]);
  const [selectedServiceGroup, setSelectedServiceGroup] = useState(null);

  /* =======================
     CHARACTER COUNT
     ======================= */
  const messageValue = watch('message', '');
  const charCount = messageValue.length;

  /* =======================
     LOAD SERVICES
     ======================= */
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/public/services-menu`
        );
        setServicesMenu(res.data || []);
      } catch {
        toast.error('Failed to load services');
      }
    };

    fetchServices();
  }, []);

  /* =======================
     SUBMIT
     ======================= */
  const onSubmit = async (data) => {
    if (charCount > MAX_CHARACTERS) {
      toast.error(`Message cannot exceed ${MAX_CHARACTERS} characters`);
      return;
    }

    setLoading(true);
    try {
      const response = await grievancesAPI.create(data);
      setReferenceId(response.data.reference_id);
      setSubmissionSuccess(true);
      toast.success(t('grievanceSubmitted'));
      reset();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit grievance');
    } finally {
      setLoading(false);
    }
  };

  /* =======================
     SUCCESS SCREEN
     ======================= */
  if (submissionSuccess) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center py-16 px-4">
          <div className="max-w-md w-full text-center">
            <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-primary mb-4">
              {t('success')}
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              {t('grievanceSubmitted')}
            </p>
            <div className="bg-secondary p-6 rounded-lg mb-6">
              <p className="text-sm text-muted-foreground mb-2">
                {t('referenceId')}
              </p>
              <p className="text-2xl font-mono font-bold text-accent">
                {referenceId}
              </p>
            </div>
            <Button
              onClick={() => setSubmissionSuccess(false)}
              className="w-full"
            >
              {t('submitGrievance')}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  /* =======================
     FORM
     ======================= */
  return (
    <div className="min-h-screen flex flex-col bg-secondary">
      <Header />

      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-primary mb-4">
              {t('grievanceTitle')}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t('grievanceDesc')}
            </p>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="bg-white rounded-lg shadow-sm border p-6 md:p-8 space-y-6"
          >
            {/* Name + Application Number */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Your Name *</Label>
                <Input {...register('name', { required: true })} className="h-12" />
              </div>

              <div>
                <Label>Application Number</Label>
                <Input
                  {...register('application_number')}
                  className="h-12"
                  placeholder="Application / Reference No."
                />
              </div>
            </div>

            {/* Email + Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  {...register('email', { required: true })}
                  className="h-12"
                />
              </div>

              <div>
                <Label>Phone Number *</Label>
                <Input
                  {...register('phone', { required: true })}
                  className="h-12"
                />
              </div>
            </div>

            {/* Service Category + Service Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Service Category *</Label>
                <select
                  {...register('service_group_id', { required: true })}
                  className="h-12 w-full rounded-md border px-3"
                  onChange={(e) => {
                    const group = servicesMenu.find(
                      (g) => g.id === e.target.value
                    );
                    setSelectedServiceGroup(group);
                  }}
                >
                  <option value="">Select Service Category</option>
                  {servicesMenu.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Service Type *</Label>
                <select
                  {...register('service_id', { required: true })}
                  className="h-12 w-full rounded-md border px-3"
                  disabled={!selectedServiceGroup}
                >
                  <option value="">Select Service Type</option>
                  {selectedServiceGroup?.services?.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Message */}
            <div>
              <Label>Message *</Label>
              <Textarea
                {...register('message', { required: true })}
                rows={6}
                maxLength={MAX_CHARACTERS}
              />
              <div className="flex justify-between text-xs mt-1">
                <span className="text-muted-foreground">
                  Maximum {MAX_CHARACTERS} characters
                </span>
                <span
                  className={
                    charCount > MAX_CHARACTERS
                      ? 'text-red-500 font-semibold'
                      : 'text-muted-foreground'
                  }
                >
                  {charCount}/{MAX_CHARACTERS}
                </span>
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading || charCount > MAX_CHARACTERS}
              className="w-full h-12 bg-accent hover:bg-accent/90"
            >
              {loading ? t('pleaseWait') : t('submit')}
            </Button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default GrievanceSubmission;